import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { StepBuilder } from '../components/StepBuilder';
import { FieldBuilder } from '../components/FieldBuilder';
import { LogicBuilder } from '../components/LogicBuilder';

import {
  ThemeSelector,
  ColorPaletteDisplay,
} from '../components/ThemeSelector';
import { ColorSystemConfig } from '../components/ColorSystemConfig';
import { DraftStatusIndicator } from '../components/DraftStatusIndicator';
import { ErrorMessageSettings } from '../components/ErrorMessageSettings';
import {
  ADVANCED_THEME_IDS,
  createThemePreset,
  getTheme,
  LanguageSelection,
  resolveBrandThemeFromAdvanced,
} from '@dynamic-flow/ui';
import {
  BrandTheme,
  type AdvancedThemeConfig,
  resolveRatingOptions,
} from '@dynamic-flow/types';
import { useFlowStore } from '../stores/flowStore';
import {
  Play,
  Save,
  Settings,
  Eye,
  GitBranch,
  Edit,
  Trash2,
} from 'lucide-react';
import { ThemeConfig, Flow as SharedFlow, Webhook, Step } from '@dynamic-flow/types'; // Use Flow type from shared package
import { flowsApi } from '../services/api';
import { getFormUrl, appConfig } from '../config/appConfig';
import WebhookForm from '@/components/WebhookForm';
import { Dialog } from 'radix-ui';
import { v4 as uuidv4 } from 'uuid';
import { useForm } from 'react-hook-form';
import { toLocalDateTimeInputValue } from '@/utils/date';
import { validatePersonFieldKeysForPublish } from '@/utils/personFieldKeys';
import { FlowBuilderProvider } from '@/contexts/FlowBuilderContext';
interface BasicFlowInformationForm {
  name: string;
  startDateTime?: string;
  endDateTime?: string;
  maximumRegister?: number;
}

interface FlowBuilderProps {
  mode?: 'edit' | 'localize';
}

function resolveBrandThemeId(
  themeId?: string,
  advancedThemeId?: string
): BrandTheme | undefined {
  const brandFromAdvanced = resolveBrandThemeFromAdvanced(advancedThemeId);
  if (brandFromAdvanced) {
    return brandFromAdvanced;
  }

  if (
    advancedThemeId === ADVANCED_THEME_IDS.CUSTOM ||
    advancedThemeId === ADVANCED_THEME_IDS.DEFAULT
  ) {
    return undefined;
  }

  return themeId === 'vtl' ? 'vtl' : themeId === 'bh' ? 'bh' : undefined;
}

export function FlowBuilder({ mode = 'edit' }: FlowBuilderProps) {
  const { flowId, languageCode = 'en' } = useParams();
  const navigate = useNavigate();
  const [selectedStepId, setSelectedStepId] = useState<string | null>(null);
  const [selectedFieldId, setSelectedFieldId] = useState<string | null>(null);
  const [selectedLanguage, setSelectedLanguage] = useState<string | undefined>(
    languageCode
  );

  const [activeTab, setActiveTab] = useState<'fields' | 'logic'>('fields');
  const [isSaving, setIsSaving] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [steps, setSteps] = useState<any[]>([]);
  const [showSettings, setShowSettings] = useState(false);
  const [advancedTheme, setAdvancedTheme] = useState<
    AdvancedThemeConfig | undefined
  >();
  const [isWebhookModalOpen, setIsWebhookModalOpen] = useState(false);
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] =
    useState(false);
  const [selectedWebhook, setSelectedWebhook] = useState<Webhook | null>(null);

  const {
    currentFlow,
    isLoading,
    error,
    setCurrentFlow,
    saveFlow,
    saveFlowTranslation,
  } = useFlowStore();

  const [publishedFlow, setPublishedFlow] = useState<SharedFlow | null>(null); // เพิ่ม state สำหรับ published flow

  const {
    register,
    handleSubmit,
    formState: { isDirty, errors },
    setValue,
  } = useForm<BasicFlowInformationForm>({
    defaultValues: {
      name: currentFlow?.title,
      startDateTime: toLocalDateTimeInputValue(currentFlow?.startDate),
      endDateTime: toLocalDateTimeInputValue(currentFlow?.endDate),
      maximumRegister: currentFlow?.maximumRegister,
    },
  });

  // Convert flow nodes to steps
  const convertNodesToSteps = (nodes: any[]): any[] => {
    const steps = nodes.map((node) => {
      // Handle landing_page and end_page types
      if (node.type === 'landing_page' || node.type === 'end_page') {
        return {
          id: node.id,
          title:
            node.data?.label ||
            (node.type === 'landing_page' ? 'Landing Page' : 'End Page'),
          description: node.data?.description || '',
          fields: [],
          type: node.type,
          coverImageUrl: node.data?.coverImageUrl || '',
          markdownContent: node.data?.markdownContent || '',
          logic: [],
        };
      }

      // Regular flow_step
      return {
        id: node.id,
        title: node.data?.label || 'Untitled Step',
        description: node.data?.description || '',
        fields: node.data?.questions
          ? node.data.questions.map((q: any) => ({
              id: q.id,
              type: q.type,
              label: q.label,
              required: q.required || false,
              placeholder: q.placeholder || '',
              options:
                q.type === 'rating' && (!q.options || q.options.length === 0)
                  ? resolveRatingOptions(undefined, q.validation)
                  : q.options || undefined,
              validation: q.validation || undefined,
              binding: q.binding || undefined, // เพิ่ม binding properties
              fieldKey: q.fieldKey,
              ...(q.type === 'consent'
                ? { consentConfig: q.consentConfig }
                : {}),
            }))
          : [],
        logic: node.data?.logic || [],
        loop: node.data?.loop || undefined,
      };
    });

    return steps;
  };

  // Convert steps to flow nodes
  const convertStepsToNodes = (steps: any[]): any[] => {
    return steps.map((step, index) => {
      // Handle landing_page and end_page types
      if (step.type === 'landing_page' || step.type === 'end_page') {
        // Extract coverImageUrl and markdownContent from step object and place them in node.data
        // FieldBuilder stores these on step.coverImageUrl and step.markdownContent
        // FormRenderer expects them in node.data.coverImageUrl and node.data.markdownContent
        const stepWithAttachments = step as any;
        return {
          id: step.id,
          type: step.type,
          position: { x: 100, y: 100 + index * 200 },
          data: {
            label: step.title,
            description: step.description || '',
            coverImageUrl: stepWithAttachments.coverImageUrl ?? '',
            markdownContent: stepWithAttachments.markdownContent ?? '',
          },
          connections:
            index < steps.length - 1
              ? [{ targetNodeId: steps[index + 1].id }]
              : [],
        };
      }

      // Regular flow_step
      return {
        id: step.id,
        type: 'flow_step',
        position: { x: 100, y: 100 + index * 200 },
        data: {
          label: step.title,
          description: step.description,
          required: true,
          questions: step.fields.map((field: any) => ({
            ...field,
            // Convert validation object to ValidationRule array format
            validation: field.validation
              ? convertValidationToRules(field.validation, field.type)
              : [],
            // Preserve binding properties
            binding: field.binding || undefined,
          })),
          logic: step.logic || [],
          loop: step.loop || undefined,
        },
        connections:
          index < steps.length - 1
            ? [{ targetNodeId: steps[index + 1].id }]
            : [],
      };
    });
  };

  // Helper function to convert validation object to ValidationRule array
  const convertValidationToRules = (
    validation: { min?: number; max?: number; pattern?: string },
    fieldType?: string
  ) => {
    const rules: any[] = [];

    if (validation.min !== undefined) {
      if (fieldType === 'number_input') {
        rules.push({
          type: 'min',
          value: validation.min,
          message: `Minimum value is ${validation.min}`,
        });
      } else {
        rules.push({
          type: 'min_length',
          value: validation.min,
          message: `Minimum length is ${validation.min} characters`,
        });
      }
    }

    if (validation.max !== undefined && fieldType !== 'rating') {
      if (fieldType === 'number_input') {
        rules.push({
          type: 'max',
          value: validation.max,
          message: `Maximum value is ${validation.max}`,
        });
      } else {
        rules.push({
          type: 'max_length',
          value: validation.max,
          message: `Maximum length is ${validation.max} characters`,
        });
      }
    }

    if (validation.pattern) {
      rules.push({
        type: 'regex',
        value: validation.pattern,
        message: 'Invalid format',
      });
    }

    return rules;
  };

  const loadFlow = useCallback(async (flowId: string, lang: string = 'en') => {
    try {
      // Load draft flow for editing
      const flow = await flowsApi.getDraft(flowId, lang);
      // Convert API response to SharedFlow type
      const sharedFlow: SharedFlow = {
        ...flow,
        createdAt: new Date(flow.createdAt),
        updatedAt: new Date(flow.updatedAt),
        publishedAt: flow.publishedAt ? new Date(flow.publishedAt) : undefined,
        startDate: flow.startDate ? new Date(flow.startDate) : undefined,
        endDate: flow.endDate ? new Date(flow.endDate) : undefined,
        theme: {
          ...flow.theme,
          typography: {
            ...flow.theme.typography,
          },
        },
      };
      setCurrentFlow(sharedFlow as any);

      // Convert nodes to steps
      if (flow.nodes && flow.nodes.length > 0) {
        const convertedSteps = convertNodesToSteps(flow.nodes);
        setSteps(convertedSteps as any);
      }

      // Set theme
      if (flow.theme?.id) {
        // setCurrentTheme(flow.theme.id); // This line was removed
      }

      // Set advanced theme if flow has theme data
      if (flow.theme) {
        if (
          (flow.theme as any)?.advanced &&
          (flow.theme as any).advanced.colors
        ) {
          // Use existing advanced theme data
          setAdvancedTheme((flow.theme as any).advanced);
        } else if (flow.theme?.colors?.primary) {
          // Create advanced theme from basic theme colors
          const primaryColor =
            typeof flow.theme.colors.primary === 'string'
              ? flow.theme.colors.primary
              : flow.theme.colors.primary.main;

          // Create a simple theme config for now
          const simpleTheme = createThemePreset(
            'custom',
            'Custom Theme',
            primaryColor,
            '#008270', // Secondary color
            flow.theme.typography?.fontFamily || 'Inter'
          );
          setAdvancedTheme(simpleTheme);
        }
      }
    } catch (err) {
      console.error('Error loading flow:', err);
    } finally {
    }
  }, []);

  // Load flow when component mounts
  useEffect(() => {
    if (flowId) {
      loadFlow(flowId);
    }
  }, [flowId]);

  // Load published flow for comparison
  useEffect(() => {
    const loadPublishedFlow = async () => {
      if (!flowId || flowId !== currentFlow?.id) {
        return;
      }
      try {
        // Load published flow for comparison
        const published = await flowsApi.getPublished(flowId);
        // Convert API response to SharedFlow type
        const sharedPublishedFlow: SharedFlow = {
          ...published,
          createdAt: new Date(published.createdAt),
          updatedAt: new Date(published.updatedAt),
          publishedAt: published.publishedAt
            ? new Date(published.publishedAt)
            : undefined,
          theme: {
            ...published.theme,
            typography: {
              ...published.theme.typography,
            },
          },
        };
        setPublishedFlow(sharedPublishedFlow);

        // Convert published nodes to steps for comparison
        if (published.nodes && published.nodes.length > 0) {
          convertNodesToSteps(published.nodes);
        }
      } catch (err) {
        // If published version doesn't exist, set to null
        setPublishedFlow(null);
      }
    };

    loadPublishedFlow();
  }, [flowId, currentFlow?.id]);

  // Auto-save with configurable interval
  useEffect(() => {
    if (!appConfig.features.autoSave) return;

    const interval = setInterval(() => {
      if (currentFlow && steps.length > 0) {
        handleSave();
      }
    }, appConfig.ui.autoSaveInterval);

    return () => clearInterval(interval);
  }, [currentFlow, steps]);

  useEffect(() => {
    setValue('name', currentFlow?.title ?? '');
    setValue(
      'startDateTime',
      toLocalDateTimeInputValue(currentFlow?.startDate)
    );
    setValue('endDateTime', toLocalDateTimeInputValue(currentFlow?.endDate));
    setValue('maximumRegister', currentFlow?.maximumRegister);
  }, [
    currentFlow?.title,
    currentFlow?.startDate,
    currentFlow?.endDate,
    currentFlow?.maximumRegister,
  ]);

  // Shared function: validates and prepares flow, then calls the provided save function
  const saveFlowWithValidation = async (
    steps: any[],
    currentFlow: any,
    saveFn: (updatedFlow: any) => Promise<void>
  ) => {
    if (!currentFlow) return;

    // 1️⃣ Validate binding fields
    const invalidBindingFields = steps.flatMap((step) =>
      step.fields.filter(
        (field: any) =>
          field.binding &&
          (!field.binding.name || field.binding.name.trim() === '')
      )
    );

    if (invalidBindingFields.length > 0) {
      alert(
        `Cannot save: ${invalidBindingFields.length} field(s) have binding enabled but no binding name. Please fix binding names before saving.`
      );
      return false;
    }

    // 2️⃣ Prepare updated flow (validation passed)
    try {
      const convertedNodes = convertStepsToNodes(steps);

      // Debug: Log landing_page and end_page data to verify it's being saved
      const landingPageNode = convertedNodes.find(
        (n: any) => n.type === 'landing_page'
      );
      const endPageNode = convertedNodes.find(
        (n: any) => n.type === 'end_page'
      );
      if (landingPageNode) {
        console.log('Saving landing_page with data:', {
          coverImageUrl: landingPageNode.data.coverImageUrl,
          markdownContent:
            landingPageNode.data.markdownContent?.substring(0, 50) + '...',
        });
      }
      if (endPageNode) {
        console.log('Saving end_page with data:', {
          coverImageUrl: endPageNode.data.coverImageUrl,
          markdownContent:
            endPageNode.data.markdownContent?.substring(0, 50) + '...',
        });
      }

      const updatedFlow = {
        ...currentFlow,
        nodes: convertedNodes,
        // Don't change status - keep it as published if it was published
        // The backend will handle saving to draft table while maintaining published status
      };

      await saveFn(updatedFlow);

      // Show success toast
      if (typeof window !== 'undefined' && (window as any).showToast) {
        (window as any).showToast('success', 'Flow saved successfully!');
      }

      return true;
    } catch (error) {
      console.error('Failed to save flow:', error);

      // Show error toast
      if (typeof window !== 'undefined' && (window as any).showToast) {
        (window as any).showToast('error', 'Failed to save flow');
      }

      return false;
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    await saveFlowWithValidation(steps, currentFlow, saveFlow);
    setIsSaving(false);
  };

  const handleSaveTranslation = async () => {
    setIsSaving(true);
    await saveFlowWithValidation(steps, currentFlow, (flow) =>
      saveFlowTranslation(flow, languageCode)
    );
    setIsSaving(false);
  };

  const handlePublish = async () => {
    if (!currentFlow) return;

    // Validate binding fields before publishing
    const invalidBindingFields = steps.flatMap((step) =>
      step.fields.filter(
        (field: any) =>
          field.binding &&
          (!field.binding.name || field.binding.name.trim() === '')
      )
    );

    if (invalidBindingFields.length > 0) {
      alert(
        `Cannot publish: ${invalidBindingFields.length} field(s) have binding enabled but no binding name. Please fix binding names before publishing.`
      );
      return;
    }

    const personKeyErrors = validatePersonFieldKeysForPublish(steps as Step[]);
    if (personKeyErrors.length > 0) {
      alert(
        `Cannot publish: person / event fieldKey issues:\n\n- ${personKeyErrors.join(
          '\n- '
        )}`
      );
      return;
    }

    try {
      setIsPublishing(true);
      const { flowsApi } = await import('../services/api');

      // Prepare flow data for publishing
      const cleanFlow = {
        title: currentFlow.title,
        description: currentFlow.description,
        nodes: convertStepsToNodes(steps),
        settings: currentFlow.settings,
        theme: currentFlow.theme,
        status: 'published' as const, // Set status to published directly with proper type
      };

      // Publish the flow using the dedicated publish endpoint
      // This writes to flow_published and syncs flow_drafts in the same transaction
      const updatedFlow = await flowsApi.publish(flowId!, cleanFlow);

      // Update current flow - convert API response to SharedFlow type
      const sharedFlow: SharedFlow = {
        ...updatedFlow,
        createdAt: new Date(updatedFlow.createdAt),
        updatedAt: new Date(updatedFlow.updatedAt),
        publishedAt: updatedFlow.publishedAt
          ? new Date(updatedFlow.publishedAt)
          : undefined,
        startDate: updatedFlow.startDate
          ? new Date(updatedFlow.startDate)
          : undefined,
        endDate: updatedFlow.endDate
          ? new Date(updatedFlow.endDate)
          : undefined,
        theme: {
          ...updatedFlow.theme,
          typography: {
            ...updatedFlow.theme.typography,
          },
        },
      };
      setCurrentFlow(sharedFlow as any);

      // Update steps state with the published flow nodes
      if (updatedFlow.nodes && updatedFlow.nodes.length > 0) {
        const convertedSteps = convertNodesToSteps(updatedFlow.nodes);
        setSteps(convertedSteps as any);
      }

      // Reload published flow for comparison
      if (flowId) {
        try {
          const published = await flowsApi.getPublished(flowId);
          const sharedPublishedFlow: SharedFlow = {
            ...published,
            createdAt: new Date(published.createdAt),
            updatedAt: new Date(published.updatedAt),
            publishedAt: published.publishedAt
              ? new Date(published.publishedAt)
              : undefined,
            theme: {
              ...published.theme,
              typography: {
                ...published.theme.typography,
              },
            },
          };
          setPublishedFlow(sharedPublishedFlow);
        } catch (err) {
          setPublishedFlow(null);
        }
      }

      // Show success toast
      if (typeof window !== 'undefined' && (window as any).showToast) {
        (window as any).showToast('success', 'Flow published successfully!');
      }
    } catch (error) {
      console.error('Failed to publish flow:', error);

      // Show error toast
      if (typeof window !== 'undefined' && (window as any).showToast) {
        (window as any).showToast('error', 'Failed to publish flow');
      }
    } finally {
      setIsPublishing(false);
    }
  };

  const handleStepsChange = (newSteps: any[]) => {
    setSteps(newSteps);
  };

  const handleStepChange = (updatedStep: any) => {
    const newSteps = steps.map((step) =>
      step.id === updatedStep.id ? updatedStep : step
    );
    setSteps(newSteps);
  };

  const selectedStep = steps.find((step) => step.id === selectedStepId);

  const handleSaveFlowInfo = async (values: BasicFlowInformationForm) => {
    if (!currentFlow) return;

    try {
      setIsSaving(true);
      const updatedFlow = {
        ...currentFlow,
        title: values.name?.trim(),
        startDate: values.startDateTime
          ? new Date(values.startDateTime)
          : undefined,
        endDate: values.endDateTime ? new Date(values.endDateTime) : undefined,
        maximumRegister: values?.maximumRegister,
      };

      // Use saveFlow to ensure store and Dashboard are updated
      await saveFlow(updatedFlow);
    } catch (error) {
      console.error('Failed to update flow name:', error);
      if (typeof window !== 'undefined' && (window as any).showToast) {
        (window as any).showToast('error', 'Failed to update flow name');
      }
    } finally {
      setIsSaving(false);
    }
  };

  // Theme handling functions
  const handleThemeChange = async (themeId: BrandTheme) => {
    if (!currentFlow) return;

    try {
      // Ensure theme object structure is correct
      const theme = getTheme(themeId);

      // Create new advanced theme based on the new basic theme's colors
      const newAdvancedTheme = createThemePreset(
        `${themeId}-preset`,
        `${theme.name} Preset`,
        theme.colors.primary.main,
        theme.colors.secondary.main,
        theme.typography.fontFamily
      );

      const updatedFlow = {
        ...currentFlow,
        theme: {
          ...theme,
          colors: theme.colors, // Save the full structured color object
          advanced: newAdvancedTheme, // Reset advanced theme to new basic theme values
        } as ThemeConfig,
      };

      // Update locally first
      setCurrentFlow(updatedFlow as any);

      // Update advanced theme state with new basic theme values
      setAdvancedTheme(newAdvancedTheme);

      // Auto-save theme change immediately without showing alert
      const saveFlow = async () => {
        try {
          const { flowsApi } = await import('../services/api');

          const cleanFlow = {
            title: updatedFlow.title,
            description: updatedFlow.description,
            nodes: convertStepsToNodes(steps),
            settings: updatedFlow.settings,
            theme: updatedFlow.theme,
            status: updatedFlow.status,
          };

          await flowsApi.update(flowId!, cleanFlow);
        } catch (error) {
          console.error('Failed to auto-save theme change:', error);
        }
      };

      // Auto-save in background
      saveFlow();
    } catch (error) {
      console.error('Failed to update theme:', error);
    }
  };

  // Advanced theme handling
  const handleAdvancedThemeChange = async (newTheme: AdvancedThemeConfig) => {
    if (!currentFlow) return;

    try {
      const updatedFlow = {
        ...currentFlow,
        theme: {
          ...currentFlow.theme,
          name: newTheme.name,
          description: newTheme.description ?? currentFlow.theme.description,
          advanced: newTheme,
          typography: {
            fontFamily: newTheme.typography.fontFamily,
            fontSizes: {
              xs: '0.75rem',
              sm: '0.875rem',
              base: '1rem',
              lg: '1.125rem',
              xl: '1.25rem',
              '2xl': '1.5rem',
              '3xl': '1.875rem',
            },
            fontWeights: { normal: 400, medium: 500, semibold: 600, bold: 700 },
          },
          spacing: {
            xs: '0.25rem',
            sm: '0.5rem',
            md: '1rem',
            lg: '1.5rem',
            xl: '2rem',
            '2xl': '3rem',
          },
          borderRadius: {
            sm: '0.25rem',
            md: '0.375rem',
            lg: '0.5rem',
            xl: '0.75rem',
          },
          shadows: {
            sm: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
            md: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
            lg: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
          },
          // Save the full structured color object
          colors: {
            primary: {
              main: newTheme.colors.primary.baseColor.hex,
              light: newTheme.colors.primary.variations[60].hex,
              dark: newTheme.colors.primary.variations[120].hex,
              contrastText: '#FFFFFF',
            },
            secondary: {
              main: newTheme.colors.information.baseColor.hex,
              light: newTheme.colors.information.variations[60].hex,
              dark: newTheme.colors.information.variations[120].hex,
              contrastText: '#FFFFFF',
            },
            background: {
              default: '#FFFFFF',
              paper: '#FFFFFF',
              accent:
                newTheme.colors.neutral.variations[20]?.hex ||
                newTheme.colors.neutral.variations[60].hex,
            },
            text: {
              primary: newTheme.colors.neutral.variations[120].hex,
              secondary: newTheme.colors.neutral.variations[100].hex,
              disabled: newTheme.colors.neutral.variations[60].hex,
            },
            border: {
              light:
                newTheme.colors.neutral.variations[20]?.hex ||
                newTheme.colors.neutral.variations[60].hex,
              main: newTheme.colors.neutral.variations[60].hex,
              dark: newTheme.colors.neutral.variations[100].hex,
            },
          },
        } as ThemeConfig,
      };

      // Update locally
      setCurrentFlow(updatedFlow as any);
      setAdvancedTheme(newTheme);

      // Auto-save theme change immediately without showing alert
      const saveFlow = async () => {
        try {
          const { flowsApi } = await import('../services/api');

          const cleanFlow = {
            title: updatedFlow.title,
            description: updatedFlow.description,
            nodes: convertStepsToNodes(steps),
            settings: updatedFlow.settings,
            theme: updatedFlow.theme,
            status: updatedFlow.status,
          };

          await flowsApi.update(flowId!, cleanFlow);
        } catch (error) {
          console.error('Failed to auto-save advanced theme change:', error);
        }
      };

      // Auto-save in background
      saveFlow();
    } catch (error) {
      console.error('Failed to update advanced theme:', error);
    }
  };

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Ctrl/Cmd + S to save
      if ((event.ctrlKey || event.metaKey) && event.key === 's') {
        event.preventDefault();
        handleSave();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleSave]);

  const handleAddWebhook = (newWebhook: Webhook) => {
    if (!currentFlow) {
      return;
    }

    newWebhook.id = uuidv4();

    const currentWebhooks = currentFlow?.settings?.webhooks ?? [];

    const newWebhooks = [...currentWebhooks, newWebhook];

    const updatedFlow: SharedFlow = {
      ...currentFlow,
      settings: {
        ...currentFlow?.settings,
        webhooks: newWebhooks,
      },
    };

    setSelectedWebhook(null);
    setCurrentFlow(updatedFlow);
  };

  const handleUpdateWebhook = (updated: Webhook) => {
    if (!currentFlow) {
      return;
    }

    const currentWebhooks = currentFlow?.settings?.webhooks ?? [];

    const newWebhooks = currentWebhooks.map((wh) =>
      wh.id === selectedWebhook?.id ? updated : wh
    );

    const updatedFlow: SharedFlow = {
      ...currentFlow,
      settings: {
        ...currentFlow?.settings,
        webhooks: newWebhooks,
      },
    };

    setSelectedWebhook(null);
    setCurrentFlow(updatedFlow);
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading flow...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 text-xl mb-2">⚠️</div>
          <p className="text-gray-600">Failed to load flow</p>
          <p className="text-sm text-gray-500 mt-1">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (!currentFlow) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">No flow found</p>
        </div>
      </div>
    );
  }

  return (
    <FlowBuilderProvider
      mode={mode}
      flowId={flowId}
      languageCode={selectedLanguage}
    >
      <div className="h-screen flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 bg-white border-b">
          <div className="flex items-center space-x-4">
            <h1 className="text-xl font-semibold">
              {currentFlow.title || 'Untitled Flow'}
            </h1>
            {mode === 'edit' && (
              <div className="flex items-center space-x-2">
                <button
                  onClick={handleSave}
                  disabled={isSaving || isPublishing}
                  className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 flex items-center gap-2 disabled:opacity-50"
                >
                  <Save className="w-4 h-4" />
                  {isSaving ? 'Saving Draft...' : 'Save Draft'}
                </button>
                <button
                  onClick={() =>
                    window.open(getFormUrl(flowId!, 'preview'), '_blank')
                  }
                  disabled={isPublishing}
                  className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 flex items-center gap-2 disabled:opacity-50"
                >
                  <Eye className="w-4 h-4" />
                  Preview
                </button>
                <button
                  onClick={handlePublish}
                  disabled={isSaving || isPublishing}
                  className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center gap-2 disabled:opacity-50"
                >
                  <Play className="w-4 h-4" />
                  {isPublishing
                    ? currentFlow?.status === 'published'
                      ? 'Updating Published...'
                      : currentFlow?.status === 'unpublished'
                        ? 'Republishing...'
                        : 'Publishing...'
                    : currentFlow?.status === 'published'
                      ? 'Update Published'
                      : currentFlow?.status === 'unpublished'
                        ? 'Republish'
                        : 'Publish'}
                </button>
              </div>
            )}
            {mode === 'localize' && (
              <button
                onClick={handleSaveTranslation}
                disabled={isSaving}
                className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 flex items-center gap-2 disabled:opacity-50"
              >
                <Save className="w-4 h-4" />
                {isSaving ? 'Saving...' : 'Save'}
              </button>
            )}
          </div>

          <div className="flex items-center space-x-2">
            <LanguageSelection
              loadFlow={(lang) => (flowId ? loadFlow(flowId, lang) : undefined)}
              value={selectedLanguage}
              onValueChange={(code) => {
                setSelectedLanguage(code);
                const basePath = `/builder/${flowId}`;
                const targetPath =
                  code === 'en' ? basePath : `${basePath}/localization/${code}`;

                navigate(targetPath, {
                  replace: true,
                });
              }}
            />
            {mode === 'edit' && (
              <button
                onClick={() => setShowSettings(!showSettings)}
                className={`p-2 border rounded-md flex items-center gap-2 ${
                  showSettings
                    ? 'bg-blue-100 border-blue-300 text-blue-700'
                    : 'border-gray-300 hover:bg-gray-50'
                }`}
              >
                <Settings className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {/* Draft Status Indicator */}
        <DraftStatusIndicator
          currentFlow={currentFlow}
          publishedFlow={publishedFlow}
        />

        {/* Settings Panel */}
        {showSettings && (
          <div className="bg-gray-50 border-b border-gray-200 p-4">
            <div className="max-w-2xl">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Flow Settings
              </h3>

              {/* Flow Name Section */}
              <div className="bg-white rounded-lg p-4 mb-4 border border-gray-200">
                <h4 className="text-sm font-medium text-gray-900 mb-3">
                  Basic Information
                </h4>
                <form onSubmit={handleSubmit(handleSaveFlowInfo)}>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Flow Name
                      </label>
                      <div className="flex items-center space-x-2">
                        <input
                          type="text"
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Enter flow name"
                          {...register('name')}
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Description
                      </label>
                      <p className="text-gray-600 text-sm">
                        {currentFlow?.description || 'No description'}
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Status
                      </label>
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          currentFlow?.status === 'published'
                            ? 'bg-green-100 text-green-800'
                            : currentFlow?.status === 'draft'
                              ? 'bg-yellow-100 text-yellow-800'
                              : currentFlow?.status === 'unpublished'
                                ? 'bg-orange-100 text-orange-800'
                                : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {currentFlow?.status === 'draft'
                          ? 'Draft'
                          : currentFlow?.status === 'unpublished'
                            ? 'Unpublished'
                            : currentFlow?.status}
                      </span>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Start Date Time
                      </label>
                      <p className="text-gray-600 text-sm">
                        <input
                          type="datetime-local"
                          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors border-gray-300`}
                          {...register('startDateTime')}
                        />
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        End Date Time
                      </label>
                      <p className="text-gray-600 text-sm">
                        <input
                          type="datetime-local"
                          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors border-gray-300`}
                          {...register('endDateTime')}
                        />
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Maximum Register
                      </label>
                      <p className="text-gray-600 text-sm">
                        <input
                          type="number"
                          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                            errors.maximumRegister
                              ? 'border-red-300 bg-red-50'
                              : 'border-gray-300'
                          }`}
                          {...register('maximumRegister', {
                            min: {
                              value: 1,
                              message:
                                'Value must be greater than or equal to 1',
                            },
                          })}
                        />
                      </p>
                      {errors.maximumRegister && (
                        <p className="mt-1 text-sm text-red-600">
                          {errors.maximumRegister?.message}
                        </p>
                      )}
                    </div>

                    <button
                      type="submit"
                      disabled={!isDirty || isSaving}
                      className="px-3 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 disabled:opacity-50 ml-auto flex"
                    >
                      Save
                    </button>
                  </div>
                </form>
              </div>

              {/* Basic Theme Settings Section */}
              <div className="bg-white rounded-lg p-4 mb-4 border border-gray-200">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-medium text-gray-900">
                    Basic Themes
                  </h4>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Quick Theme Selection
                    </label>
                    <ThemeSelector
                      selectedThemeId={
                        resolveBrandThemeId(
                          currentFlow?.theme?.id,
                          advancedTheme?.id
                        ) ?? ''
                      }
                      onThemeChange={handleThemeChange}
                    />
                  </div>

                  {/* Color Palette Preview */}
                  <div>
                    {resolveBrandThemeId(
                      currentFlow?.theme?.id,
                      advancedTheme?.id
                    ) ? (
                      <ColorPaletteDisplay
                        themeId={
                          resolveBrandThemeId(
                            currentFlow?.theme?.id,
                            advancedTheme?.id
                          )!
                        }
                      />
                    ) : (
                      <p className="text-xs text-gray-500">
                        Brand palette preview is hidden while using{' '}
                        {advancedTheme?.name ?? 'a custom theme'}.
                      </p>
                    )}
                  </div>

                  <div className="text-xs text-gray-500">
                    <p>
                      Quick themes for common use cases. For advanced
                      customization, use the Advanced Color System below.
                    </p>
                  </div>
                </div>
              </div>

              {/* Advanced Color System */}
              <div className="bg-white rounded-lg mb-4 border border-gray-200">
                <div className="flex items-center justify-between p-4 border-b border-gray-200">
                  <h4 className="text-sm font-medium text-gray-900">
                    Advanced Color System
                  </h4>
                </div>
                <ColorSystemConfig
                  currentTheme={advancedTheme}
                  onThemeChange={handleAdvancedThemeChange}
                />
              </div>

              {/* Error Message Settings */}
              <div className="bg-white rounded-lg mb-4 border border-gray-200">
                <div className="p-4">
                  <ErrorMessageSettings
                    errorMessages={currentFlow?.settings?.errorMessages}
                    onErrorMessagesChange={(errorMessages) => {
                      const updatedFlow = {
                        ...currentFlow,
                        settings: {
                          ...currentFlow?.settings,
                          errorMessages,
                        },
                      };
                      setCurrentFlow(updatedFlow);
                    }}
                  />
                </div>
              </div>

              {/* Webhook Settings */}
              <div className="bg-white rounded-lg mb-4 border border-gray-200">
                <div className="p-4 flex flex-col gap-2">
                  <Dialog.Root
                    open={isWebhookModalOpen}
                    onOpenChange={(isOpen) => {
                      setIsWebhookModalOpen(isOpen);
                      if (!isOpen) {
                        setSelectedWebhook(null);
                      }
                    }}
                  >
                    <div className="flex justify-between">
                      <p>Webhooks</p>
                      <Dialog.Trigger asChild>
                        <button className="px-3 py-1 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700">
                          Add
                        </button>
                      </Dialog.Trigger>
                    </div>

                    <Dialog.Portal>
                      <Dialog.Overlay className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40" />
                      <Dialog.Content className="fixed left-1/2 top-1/2 z-50 grid w-full max-w-lg -translate-x-1/2 -translate-y-1/2 gap-4 border bg-white p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] rounded-lg">
                        <div className="flex items-center justify-between">
                          <Dialog.Title className="text-lg font-semibold">
                            Webhook
                          </Dialog.Title>
                          <Dialog.Close className="rounded-sm opacity-70 ring-offset-white transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-gray-950 focus:ring-offset-2 disabled:pointer-events-none">
                            <svg
                              className="h-4 w-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M6 18L18 6M6 6l12 12"
                              />
                            </svg>
                            <span className="sr-only">Close</span>
                          </Dialog.Close>
                        </div>

                        <WebhookForm
                          editMode={!!selectedWebhook}
                          defaultValues={selectedWebhook || {}}
                          onSubmit={(data) => {
                            if (selectedWebhook) {
                              handleUpdateWebhook(data);
                            } else {
                              handleAddWebhook(data);
                            }
                            setIsWebhookModalOpen(false);
                          }}
                        />
                      </Dialog.Content>
                    </Dialog.Portal>

                    {currentFlow?.settings?.webhooks?.map((webhook, i) => {
                      return (
                        <div
                          key={i}
                          className="flex justify-between border border-grey-200 rounded p-2 align-top"
                        >
                          <div className="flex flex-col gap-1">
                            <div>{webhook?.name}</div>
                            <small className="bg-gray-200 rounded py-1 px-2">
                              {webhook?.url}
                            </small>
                          </div>
                          <div className="flex gap-2 items-center">
                            <button
                              className="h-fit p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                              onClick={() => {
                                setSelectedWebhook(webhook);
                                setIsWebhookModalOpen(true);
                              }}
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              className="h-fit p-2 rounded text-red-400 hover:text-red-600 hover:bg-red-100"
                              onClick={() => {
                                setSelectedWebhook(webhook);
                                setIsDeleteConfirmationOpen(true);
                              }}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </Dialog.Root>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Main Content */}
        <div className="flex-1 flex">
          {/* Left Sidebar - Step Builder */}
          <div className="w-80 bg-white border-r border-gray-200">
            <StepBuilder
              steps={steps}
              selectedStepId={selectedStepId}
              onStepsChange={handleStepsChange}
              onSelectStep={(stepId) => {
                setSelectedStepId(stepId);
                setSelectedFieldId(null); // Reset field selection
              }}
            />
          </div>

          {/* Main Area - Field Builder & Logic */}
          <div className="flex-1 bg-white border-r border-gray-200 flex flex-col">
            {/* Tabs */}
            <div className="border-b border-gray-200">
              <nav className="flex space-x-8 px-4">
                <button
                  onClick={() => setActiveTab('fields')}
                  className={`py-3 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'fields'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Fields & Content
                </button>
                <button
                  onClick={() => setActiveTab('logic')}
                  className={`py-3 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
                    activeTab === 'logic'
                      ? 'border-purple-500 text-purple-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <GitBranch className="w-4 h-4" />
                  Logic & Conditions
                </button>
              </nav>
            </div>

            {/* Tab Content */}
            <div className="flex-1">
              {activeTab === 'fields' ? (
                <FieldBuilder
                  step={selectedStep || null}
                  selectedFieldId={selectedFieldId}
                  onStepChange={handleStepChange}
                  onSelectField={setSelectedFieldId}
                  allSteps={steps}
                />
              ) : (
                <LogicBuilder
                  steps={steps}
                  selectedStepId={selectedStepId}
                  onStepsChange={handleStepsChange}
                />
              )}
            </div>
          </div>
        </div>

        <Dialog.Root
          open={isDeleteConfirmationOpen}
          onOpenChange={setIsDeleteConfirmationOpen}
        >
          <Dialog.Portal>
            <Dialog.Overlay className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40" />
            <Dialog.Content className="fixed left-1/2 top-1/2 z-50 grid w-full max-w-lg -translate-x-1/2 -translate-y-1/2 gap-4 border bg-white p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] rounded-lg">
              <div className="flex items-center justify-between">
                <Dialog.Title className="text-lg font-semibold">
                  Are you sure you want to delete?
                </Dialog.Title>
                <Dialog.Close className="rounded-sm opacity-70 ring-offset-white transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-gray-950 focus:ring-offset-2 disabled:pointer-events-none">
                  <svg
                    className="h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                  <span className="sr-only">Close</span>
                </Dialog.Close>
              </div>

              <div className="flex gap-2 justify-end">
                <Dialog.Close asChild>
                  <button className="border py-1 px-4 rounded hover:bg-gray-100">
                    Cancel
                  </button>
                </Dialog.Close>
                <button
                  className="bg-red-600 text-white rounded py-1 px-4 hover:bg-red-800"
                  onClick={() => {
                    if (!currentFlow) {
                      return;
                    }

                    const newWebhooks = currentFlow?.settings?.webhooks?.filter(
                      (webhook) => webhook.id !== selectedWebhook?.id
                    );

                    const updatedFlow: SharedFlow = {
                      ...currentFlow,
                      settings: {
                        ...currentFlow?.settings,
                        webhooks: newWebhooks ?? [],
                      },
                    };

                    setCurrentFlow(updatedFlow);

                    setIsDeleteConfirmationOpen(false);
                  }}
                >
                  Delete
                </button>
              </div>
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>
      </div>
    </FlowBuilderProvider>
  );
}
